import { FC, ReactNode } from 'react';

import { Container } from '../common/Container';
import { Flex } from '../common/Flex';
import { Slider } from '../common/Slider';
import { Text } from '../common/Text';
import { WhySgHeroBarChartIcon, WhySgHeroRoutingIcon } from '../whySg/icons/WhySgHeroIcons';
import { WhySgShieldCheckHeroIcon } from '../whySg/icons/WhySgShieldCheckIcons';

import styles from './WhySgHero.module.scss';

type FeatureItem = {
  category: string;
  title: string;
  description: string;
  icon: FC;
};

const FEATURES: FeatureItem[] = [
  {
    category: 'Technology',
    title: 'Enterprise Security',
    description: 'PCI DSS Level 1 compliant with tokenization and 3D Secure support built in.',
    icon: WhySgShieldCheckHeroIcon,
  },
  {
    category: 'Compliance',
    title: 'Smart Routing',
    description: 'Automatically route transactions to the best-performing acquirer based on real-time data.',
    icon: WhySgHeroRoutingIcon,
  },
  {
    category: 'Analytics',
    title: 'Unified Analytics',
    description: 'One dashboard for all payment providers with real-time insights and reconciliation.',
    icon: WhySgHeroBarChartIcon,
  },
];

type FeatureSlideCardProps = Omit<FeatureItem, 'icon'> & {
  icon: ReactNode;
};

function FeatureSlideCard(props: FeatureSlideCardProps) {
  const { category, title, description, icon } = props;

  return (
    <article className={styles.card}>
      <Flex className={styles.cardInner} direction={'column'} alignItems={'flexStart'} gap={4}>
        <Flex className={styles.iconWrap} alignItems={'center'} justifyContent={'center'} aria-hidden>
          {icon}
        </Flex>

        <Text type={'medium14'} className={styles.category}>
          {category}
        </Text>

        <Text type={'h3'} color={'white'}>
          {title}
        </Text>

        <Text type={'regular14'} color={'white50'} whiteSpace={'normal'}>
          {description}
        </Text>
      </Flex>
    </article>
  );
}

export const WhySgHero: FC = () => {
  return (
    <Container className={styles.root}>
      <Flex direction={'column'} alignItems={'center'} className={styles.intro}>
        <Text type={'medium14'} align={'center'} className={styles.eyebrow}>
          Why SmartGates?
        </Text>

        <Text type={'medium64'} color={'white'} align={'center'} className={styles.headline}>
          Scale your business with an intelligent data-driven payment ecosystem
        </Text>

        <Text type={'regular16'} color={'white50'} align={'center'} whiteSpace={'normal'} className={styles.subhead}>
          Connect to multiple acquirers and payment methods via a single integration. Get clear reporting, smart routing
          and enterprise-grade security from day one.
        </Text>

        <Slider
          navigation
          className={styles.slider}
          items={FEATURES.map(({ icon: Icon, ...rest }) => ({
            id: rest.title,
            content: <FeatureSlideCard {...rest} icon={<Icon />} />,
          }))}
          slidesPerView={1}
          spaceBetween={12}
          breakpoints={{
            960: {
              slidesPerView: 2,
              spaceBetween: 20,
            },
          }}
        />
      </Flex>
    </Container>
  );
};
